import React, { memo, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { CareerIcon } from 'atoms';
import { useSetCareerLessonBookmark } from 'core/hooks/CareerCenter';

export interface ICareerBookmarkButton {
  lessonId: string;
  /** Current persisted state; drives the ☆/★ glyph. */
  isBookmarked: boolean;
}

/**
 * ☆/★ bookmark toggle for an unlocked career lesson (spec 0020 Фаза C).
 *
 * Optimistic: the glyph flips immediately, then reconciles with the server's
 * echoed `isBookmarked` (or reverts on a network failure). Local state stays in
 * sync with the prop so a `cache-and-network` refetch of the lesson updates it.
 */
export const CareerBookmarkButton = memo(({ lessonId, isBookmarked }: ICareerBookmarkButton) => {
  const { t } = useTranslation();
  const { setBookmark, loading } = useSetCareerLessonBookmark();

  const [bookmarked, setBookmarked] = useState(isBookmarked);

  // Reconcile with the authoritative prop whenever it changes (e.g. refetch).
  useEffect(() => {
    setBookmarked(isBookmarked);
  }, [isBookmarked]);

  const onToggle = useCallback(async () => {
    if (loading) {
      return;
    }

    const next = !bookmarked;
    setBookmarked(next); // optimistic

    try {
      const result = await setBookmark(lessonId, next);
      setBookmarked(result.isBookmarked);
    } catch {
      setBookmarked(!next); // revert on transport failure
    }
  }, [bookmarked, lessonId, loading, setBookmark]);

  const label = bookmarked
    ? t('careerCenter.lesson.bookmark.remove')
    : t('careerCenter.lesson.bookmark.add');

  return (
    <ToggleButton
      type="button"
      onClick={onToggle}
      disabled={loading}
      isActive={bookmarked}
      aria-pressed={bookmarked}
      title={label}>
      <Star>
        <CareerIcon name={bookmarked ? 'starFilled' : 'star'} size={18} />
      </Star>
      <LabelText>{label}</LabelText>
    </ToggleButton>
  );
});

const ToggleButton = styled.button<{ isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid ${({ isActive }) => (isActive ? Colors.main : Colors.system_gray)};
  border-radius: 8px;
  background-color: ${({ isActive }) => (isActive ? Colors.main_light : Colors.white)};
  color: ${({ isActive }) => (isActive ? Colors.main_dark : Colors.gray_dark)};
  cursor: pointer;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover:not(:disabled) {
    border-color: ${Colors.main};
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

const Star = styled.span`
  font-size: 18px;
  line-height: 1;
  color: ${Colors.main};
`;

const LabelText = styled.span`
  white-space: nowrap;
`;
